import { ActiveTool } from 'src/components/viewers/SheetsViewerV2/enums/ActiveTool';
import { AggregatedView } from 'src/components/viewers/SheetsViewerV2/hooks/useSheetsAggregatedView';
import { ISheetShape } from 'src/components/viewers/SheetsViewerV2/shapes/abstract/AbstractSheetShape';
import {
  CROSSHAIR_CURSOR,
  VIEWER_TOP_OFFSET,
} from 'src/components/viewers/SheetsViewerV2/SheetsViewerV2.config';
import { ApiCoordinatesConverter } from 'src/components/viewers/SheetsViewerV2/utils/apiCoordinatesConverter';
import Edit2D = Autodesk.Extensions.Edit2D;

type DbId = number;

interface BoxSelectionState {
  isActive: boolean;
  startPoint: { x: number; y: number } | null;
  currentPoint: { x: number; y: number } | null;
  selectionRectangle: any | null;
}

export class SelectTool extends Autodesk.Viewing.ToolInterface {
  names = [ActiveTool.Select];

  private _hovered: ISheetShape | null = null;
  private _selected: Map<DbId, ISheetShape> = new Map();
  private _boxSelection: BoxSelectionState = {
    isActive: false,
    startPoint: null,
    currentPoint: null,
    selectionRectangle: null,
  };
  private _overlayName = 'box-selection-overlay';

  constructor(
    private readonly viewer: AggregatedView['viewer'],
    private readonly shapes: ISheetShape[],
    private readonly activeSheetData: import('src/components/viewers/SheetsViewerV2/hooks/useActiveSheet').ActiveSheetData,
    private readonly edit2d: Edit2D,
    private readonly onSelect?: (shape: ISheetShape[]) => void,
    private readonly onHover?: (shape: ISheetShape | null) => void
  ) {
    super();

    delete (this as any).handleSingleClick;
    delete (this as any).handleMouseMove;
    delete (this as any).handleButtonDown;
    delete (this as any).handleButtonUp;
    delete (this as any).getPriority;
    delete (this as any).activate;
    delete (this as any).deactivate;
  }

  getPriority(): number {
    // Higher priority than default tools to intercept events first
    return 100;
  }

  activate(): void {
    // Disable default selection to prevent interference
    this.viewer.setSelectionMode(Autodesk.Viewing.SelectionMode.MIXED);

    // Ensure overlay scene exists for Three.js fallback
    try {
      this.viewer.overlays.addScene(this._overlayName);
    } catch (error) {
      // Scene might already exist, ignore error
    }

    // Synchronize the tool's internal selection state with the current shape selection states.
    // This is needed so that shift+click can build upon existing selections.
    this.syncWithCurrentSelection();
  }

  deactivate(): void {
    this._clearBoxSelectionOverlay();
    this._boxSelection.isActive = false;
    this._boxSelection.startPoint = null;
    this._boxSelection.currentPoint = null;

    // Re-enable default selection
    this.viewer.setSelectionMode(Autodesk.Viewing.SelectionMode.REGULAR);
  }

  getCursor(): string {
    return this._boxSelection.isActive ? 'crosshair' : CROSSHAIR_CURSOR;
  }

  handleButtonDown(event: MouseEvent, button: number): boolean {
    if (button !== 0) return false;

    const clientX = event.clientX;
    const clientY = event.clientY - VIEWER_TOP_OFFSET;

    // Check if Ctrl/Cmd is held for box selection
    if (event.ctrlKey || event.metaKey) {
      this._startBoxSelection(clientX, clientY);
      return true;
    }

    return false;
  }

  handleButtonUp(event: MouseEvent, button: number): boolean {
    if (button !== 0) return false;

    if (this._boxSelection.isActive) {
      this._endBoxSelection(event.clientX, event.clientY - VIEWER_TOP_OFFSET);
      return true;
    }

    return false;
  }

  handleSingleClick(event: MouseEvent, button: number): boolean {
    const clientX = event.clientX;
    const clientY = event.clientY - VIEWER_TOP_OFFSET;
    const cursorPosition = this.viewer.impl.intersectGround(clientX, clientY);

    if (button !== 0 || !cursorPosition) return false;

    // Skip normal click handling if we just finished box selection
    if (this._boxSelection.isActive) return false;

    // Find all shapes that pass the hit test
    const hitShapes = this.shapes.filter((shape) =>
      shape.hitTest(cursorPosition.x, cursorPosition.y)
    );

    // Select the shape with the highest priority (same logic as hover)
    const hitShape = this._getHighestPriorityShape(hitShapes);

    return this._handleShapeClick(event, hitShape);
  }

  private _handleShapeClick(
    event: MouseEvent,
    hitShape: ISheetShape | null
  ): boolean {
    if (event.shiftKey && !hitShape) {
      return false;
    } else if (event.shiftKey && hitShape) {
      this._toggleShapeSelection(hitShape);
    } else if (hitShape) {
      this._selectSingleShape(hitShape);
    } else {
      this._clearSelection();
    }
    return true;
  }

  private _toggleShapeSelection(shape: ISheetShape): void {
    if (this._selected.has(shape.dbId)) {
      shape.setSelected(false);
      this._selected.delete(shape.dbId);
    } else {
      shape.setSelected(true);
      this._selected.set(shape.dbId, shape);
    }
    this.onSelect?.([...this._selected.values()]);
  }

  private _selectSingleShape(shape: ISheetShape): void {
    this._selected.forEach((selected) => selected.setSelected(false));
    this._selected = new Map([[shape.dbId, shape]]);
    this.onSelect?.([...this._selected.values()]);
  }

  private _clearSelection(): void {
    this._selected.forEach((selected) => selected.setSelected(false));
    this._selected = new Map();
    this.onSelect?.([...this._selected.values()]);
  }

  handleMouseMove(event: MouseEvent): boolean {
    const clientX = event.clientX;
    const clientY = event.clientY - VIEWER_TOP_OFFSET;

    // Handle box selection drag
    if (this._boxSelection.isActive) {
      this._updateBoxSelection(clientX, clientY);
      return true; // Prevent smooth panning during box selection
    }

    // Normal hover behavior
    const cursorPosition = this.viewer.impl.intersectGround(clientX, clientY);

    if (!cursorPosition) {
      return false;
    }

    // Find all shapes that pass the hit test
    const hitShapes = this.shapes.filter((shape) =>
      shape.hitTest(cursorPosition.x, cursorPosition.y)
    );

    // Select the shape with the highest hover priority
    const hitShape = this._getHighestPriorityShape(hitShapes);

    if (hitShape !== this._hovered) {
      this._hovered?.setHovered(false);
      this._hovered = hitShape;
      hitShape?.setHovered(true);
      this.onHover?.(hitShape);
    }

    return false; // When set to true, smooth panning is disabled.
  }

  /**
   * Get the shape with the highest hover priority from a list of shapes.
   * Priority order (from lowest to highest): Polygon < Lines < Split lines < Points
   */
  private _getHighestPriorityShape(shapes: ISheetShape[]): ISheetShape | null {
    if (shapes.length === 0) return null;
    if (shapes.length === 1) return shapes[0];

    // Define priority values (higher number = higher priority)
    const getShapePriority = (shape: ISheetShape): number => {
      const apiModel = shape.apiModel;
      if (!apiModel) return 0;

      // Check shape types in priority order (highest to lowest)
      if (apiModel.sheetShapePoint) return 4; // Points - highest priority
      if (apiModel.sheetShapeLine) return 3; // Lines
      if (apiModel.sheetShapeSplitLine) return 2; // Split lines
      if (apiModel.sheetShapePolygon) return 1; // Polygons - lowest priority

      return 0; // Unknown shape type
    };

    // Find the shape with the highest priority
    let highestPriorityShape = shapes[0];
    let highestPriority = getShapePriority(shapes[0]);

    for (let i = 1; i < shapes.length; i++) {
      const priority = getShapePriority(shapes[i]);
      if (priority > highestPriority) {
        highestPriority = priority;
        highestPriorityShape = shapes[i];
      }
    }

    return highestPriorityShape;
  }

  private _startBoxSelection(x: number, y: number): void {
    this._boxSelection.isActive = true;
    this._boxSelection.startPoint = { x, y };
    this._boxSelection.currentPoint = { x, y };

    // Clear existing selection visualization
    this._clearBoxSelectionOverlay();
  }

  private _updateBoxSelection(x: number, y: number): void {
    if (!this._boxSelection.isActive || !this._boxSelection.startPoint) return;

    this._boxSelection.currentPoint = { x, y };
    this._drawSelectionRectangle();
  }

  private _endBoxSelection(x: number, y: number): void {
    if (!this._boxSelection.isActive || !this._boxSelection.startPoint) return;

    this._boxSelection.currentPoint = { x, y };

    // Perform box selection
    const selectedShapes = this._getShapesInBox();

    // Clear existing selection
    this._selected.forEach((selected) => selected.setSelected(false));
    this._selected = new Map();

    // Set new selection
    selectedShapes.forEach((shape) => {
      shape.setSelected(true);
      this._selected.set(shape.dbId, shape);
    });

    this.onSelect?.(selectedShapes);

    // Clean up box selection state
    this._clearBoxSelectionOverlay();
    this._boxSelection.isActive = false;
    this._boxSelection.startPoint = null;
    this._boxSelection.currentPoint = null;
  }

  private _drawSelectionRectangle(): void {
    if (!this._boxSelection.startPoint || !this._boxSelection.currentPoint) {
      return;
    }

    this._clearBoxSelectionOverlay();

    const start = this._boxSelection.startPoint;
    const current = this._boxSelection.currentPoint;

    // Convert screen coordinates to world coordinates for drawing
    const startWorld = this.viewer.impl.clientToWorld(start.x, start.y);
    const currentWorld = this.viewer.impl.clientToWorld(current.x, current.y);

    if (!startWorld?.point || !currentWorld?.point) return;

    // Use Three.js approach directly for now (Edit2D has styling issues)
    this._drawSelectionRectangleFallback(start, current);
  }

  private _drawSelectionRectangleFallback(
    start: { x: number; y: number },
    end: { x: number; y: number }
  ): void {
    // Convert screen coordinates to world coordinates
    const startWorld = this.viewer.impl.clientToWorld(start.x, start.y);
    const endWorld = this.viewer.impl.clientToWorld(end.x, end.y);
    if (!startWorld?.point || !endWorld?.point) return;

    // Determine drag direction and color
    const isRightToLeft = start.x > end.x;
    const color = isRightToLeft ? 0x00cc44 : 0x0088ff;
    const fillOpacity = 0.15;
    const outlineOpacity = 0.8;

    // Rectangle corners (clockwise)
    const [x1, y1, x2, y2] = [
      startWorld.point.x,
      startWorld.point.y,
      endWorld.point.x,
      endWorld.point.y,
    ];
    const corners = [
      [x1, y1, 0],
      [x2, y1, 0],
      [x2, y2, 0],
      [x1, y2, 0],
    ];

    function createFillMesh(corners: number[][]) {
      const fillGeometry = new THREE.BufferGeometry();
      const fillVertices = new Float32Array([
        ...corners[0],
        ...corners[1],
        ...corners[2],
        ...corners[0],
        ...corners[2],
        ...corners[3],
      ]);
      (fillGeometry as any).setAttribute(
        'position',
        new THREE.BufferAttribute(fillVertices, 3)
      );
      const fillMaterial = new THREE.MeshBasicMaterial({
        color,
        opacity: fillOpacity,
        transparent: true,
        side: THREE.DoubleSide,
        depthWrite: false,
      });
      const mesh = new THREE.Mesh(fillGeometry, fillMaterial);
      mesh.position.z = -0.001;
      return mesh;
    }

    function createOutlineLines(corners: number[][]) {
      const outlineMaterial = new THREE.LineBasicMaterial({
        color,
        opacity: outlineOpacity,
        transparent: true,
        depthWrite: false,
      });
      return Array.from({ length: 4 }, (_, i) => {
        const geometry = new THREE.BufferGeometry();
        const a = corners[i];
        const b = corners[(i + 1) % 4];
        (geometry as any).setAttribute(
          'position',
          new THREE.BufferAttribute(new Float32Array([...a, ...b]), 3)
        );
        return new THREE.Line(geometry, outlineMaterial);
      });
    }

    const rectangleGroup = new THREE.Group();
    rectangleGroup.add(createFillMesh(corners));
    createOutlineLines(corners).forEach((line) => rectangleGroup.add(line));

    this._boxSelection.selectionRectangle = rectangleGroup;
    this.viewer.overlays.addMesh(
      this._boxSelection.selectionRectangle as any,
      this._overlayName
    );
  }

  private _clearBoxSelectionOverlay(): void {
    if (this._boxSelection.selectionRectangle) {
      try {
        this.viewer.overlays.removeMesh(
          this._boxSelection.selectionRectangle as any,
          this._overlayName
        );
      } catch (error) {
        console.warn('Could not remove rectangle:', error);
      }

      this._boxSelection.selectionRectangle = null;
    }
  }

  private _getShapesInBox(): ISheetShape[] {
    if (!this._boxSelection.startPoint || !this._boxSelection.currentPoint)
      return [];

    const start = this._boxSelection.startPoint;
    const end = this._boxSelection.currentPoint;
    const isRightToLeft = start.x > end.x;

    // Convert screen coordinates to API coordinates
    const startApi = this._screenToApiCoordinates(start.x, start.y);
    const endApi = this._screenToApiCoordinates(end.x, end.y);
    if (!startApi || !endApi) return [];

    const minX = Math.min(startApi.x, endApi.x);
    const maxX = Math.max(startApi.x, endApi.x);
    const minY = Math.min(startApi.y, endApi.y);
    const maxY = Math.max(startApi.y, endApi.y);

    // Only select if there's a meaningful box size
    if (Math.abs(maxX - minX) < 5 || Math.abs(maxY - minY) < 5) return [];

    if (isRightToLeft) {
      // Select all shapes that intersect the box
      return this.shapes.filter((shape) =>
        this._isShapeIntersectingBox(shape, minX, maxX, minY, maxY)
      );
    } else {
      // Select only shapes completely inside the box
      return this.shapes.filter((shape) =>
        this._isShapeCompletelyInBox(shape, minX, maxX, minY, maxY)
      );
    }
  }

  private _screenToApiCoordinates(
    screenX: number,
    screenY: number
  ): { x: number; y: number } | null {
    try {
      // Use the same coordinate converter that shapes use for proper calibration handling
      const converter = new ApiCoordinatesConverter(
        this.viewer,
        this.activeSheetData.viewport
      );

      // Convert screen to world coordinates first - NO VIEWER_TOP_OFFSET here as it's already handled
      const result = this.viewer.impl.clientToWorld(screenX, screenY);

      if (!result || !result.point) {
        console.warn(
          'No world point found for screen coordinates:',
          screenX,
          screenY
        );
        return null;
      }

      // Convert world coordinates to API coordinates using the proper converter
      const apiPoint = converter.convertToApiPoint(result.point);
      return { x: apiPoint.x, y: apiPoint.y };
    } catch (error) {
      console.warn('Error converting screen to API coordinates:', error);
      return null;
    }
  }

  private _isShapeCompletelyInBox(
    shape: ISheetShape,
    minX: number,
    maxX: number,
    minY: number,
    maxY: number
  ): boolean {
    // Get shape's bounding box in API coordinates (same as selection box)
    const shapeBounds = this._getShapeApiBounds(shape);
    if (!shapeBounds) return false;

    // Add small tolerance for coordinate precision issues
    const tolerance = 1.0;

    // Check if shape is completely inside the selection box (with tolerance)
    const isInside =
      shapeBounds.minX >= minX - tolerance &&
      shapeBounds.maxX <= maxX + tolerance &&
      shapeBounds.minY >= minY - tolerance &&
      shapeBounds.maxY <= maxY + tolerance;

    return isInside;
  }

  private _getShapeApiBounds(shape: ISheetShape): {
    minX: number;
    maxX: number;
    minY: number;
    maxY: number;
  } | null {
    try {
      // Get shape's coordinates and convert them to world space using the same
      // conversion approach that shapes use for rendering
      if (!shape.apiModel) return null;

      let apiPoints: { x: number; y: number }[] = [];

      if (shape.apiModel.sheetShapePolygon) {
        const multipolygon = shape.apiModel.sheetShapePolygon.multipolygon;
        apiPoints = multipolygon.polygons.flatMap((polygon) =>
          polygon.exterior.points.map((point: any) => ({
            x: point.x,
            y: point.y,
          }))
        );
      } else if (shape.apiModel.sheetShapeLine) {
        const points = shape.apiModel.sheetShapeLine.points;
        apiPoints = points.map((point: any) => ({
          x: point.x,
          y: point.y,
        }));
      } else if (shape.apiModel.sheetShapeSplitLine) {
        const points = shape.apiModel.sheetShapeSplitLine.points;
        apiPoints = points.map((point: any) => ({
          x: point.x,
          y: point.y,
        }));
      } else if (shape.apiModel.sheetShapePoint) {
        const point = shape.apiModel.sheetShapePoint.point;
        apiPoints = [{ x: point.x, y: point.y }];
      }

      if (apiPoints.length === 0) return null;

      // Calculate bounding box directly from API coordinates (same space as selection box)
      const xs = apiPoints.map((p) => p.x);
      const ys = apiPoints.map((p) => p.y);

      const bounds = {
        minX: Math.min(...xs),
        maxX: Math.max(...xs),
        minY: Math.min(...ys),
        maxY: Math.max(...ys),
      };

      return bounds;
    } catch (error) {
      console.warn('Error calculating shape world bounds:', error);
      return null;
    }
  }

  private _apiToWorldCoordinates(
    apiX: number,
    apiY: number
  ): { x: number; y: number } | null {
    try {
      // This is a placeholder - we'd need access to the same coordinate converter
      // that the shapes use. For now, let's try a direct approach.
      const worldIntersection = this.viewer.impl.intersectGround(apiX, apiY);

      if (!worldIntersection) {
        // Fallback: assume API coordinates are close to world coordinates
        return { x: apiX, y: apiY };
      }

      return { x: worldIntersection.x, y: worldIntersection.y };
    } catch (error) {
      console.warn('Error converting API to world coordinates:', error);
      return { x: apiX, y: apiY };
    }
  }

  // Helper: check if a shape intersects the selection box (API coordinates)
  private _isShapeIntersectingBox(
    shape: ISheetShape,
    minX: number,
    maxX: number,
    minY: number,
    maxY: number
  ): boolean {
    const shapeBounds = this._getShapeApiBounds(shape);
    if (!shapeBounds) return false;
    // Check for any overlap
    return !(
      shapeBounds.maxX < minX ||
      shapeBounds.minX > maxX ||
      shapeBounds.maxY < minY ||
      shapeBounds.minY > maxY
    );
  }

  /**
   * Synchronize the tool's internal selection state with the current shape selection states.
   * This is needed so that shift+click can build upon existing selections.
   */
  syncWithCurrentSelection(): void {
    this._selected.clear();

    // Find all currently selected shapes and add them to our internal selection
    this.shapes.forEach((shape) => {
      if (shape.selected) {
        this._selected.set(shape.dbId, shape);
      }
    });
  }
}
